import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { handleForgotPassword } from '../utils/api-handlers/authentication-forms';

const forgotPasswordSchema = z.object({
  email: z.string()
    .min(1, '請輸入信箱')
    .email('信箱格式不正確')
});

const COUNTDOWN_TIME = 30;

function ForgotPassword() {
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [countdown, setCountdown] = useState(0);
  const [lastRequestTime, setLastRequestTime] = useState(null);
  const { isAuthenticated, user } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    watch
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: ''
    }
  });

  // 檢查是否可以發送新的請求
  const canSendRequest = () => {
    if (!lastRequestTime) return true;
    const timeSinceLastRequest = (Date.now() - lastRequestTime) / 1000;
    return timeSinceLastRequest >= COUNTDOWN_TIME;
  };

  // 處理倒數計時
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setVerificationStatus('idle');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown]);

  const isButtonDisabled = isSubmitting || verificationStatus === 'sending' || !canSendRequest() || countdown > 0;
  const getButtonText = () => {
    if (verificationStatus === 'sending') return '發送中...';
    if (countdown > 0) return `重新發送 (${countdown}s)`;
    if (!canSendRequest()) return `請稍後再試 (${countdown}s)`;
    return '發送重設連結';
  };

  const onSubmit = async (data) => {
    try {
      const result = await handleForgotPassword(
        data.email,
        navigate,
        setVerificationStatus,
      );
      if (result?.success) {
        setLastRequestTime(Date.now());
        setCountdown(COUNTDOWN_TIME);
        setVerificationStatus('sent');      }
    } catch (error) {
      setVerificationStatus('error');
      console.error('Forgot password error:', error);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            忘記密碼
          </h2>
          {/* <p className="mt-2 text-center text-sm text-gray-600">
            請輸入您的信箱，我們會寄送重設密碼的連結給您
          </p> */}
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              信箱
            </label>
            <input
              id="email"
              type="email"
              {...register('email')}
              placeholder="請輸入信箱"
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                errors.email ? 'border-red-500' : ''
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isButtonDisabled}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              {getButtonText()}
            </button>
          </div>

          <div className="flex justify-end">
            <Link
              to="/login"
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              返回登入
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ForgotPassword;